import type { ImageProps } from 'fitify-ui-onboarding/src/types/ui'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { useCallback, useMemo } from 'react'

import { DEFAULT_IMAGE_VARIANT } from 'data/images'
import type {
  CategoryPageImages,
  CategoryPages,
  CategoryVariants,
  ImageCategories,
} from 'types/images'
import { Question } from 'types/state'
import { getImageProps } from 'utils/images'
import { useDataState } from 'utils/state'

export const useCategoryImageVariant = <TCategory extends ImageCategories>(
  category?: TCategory
): string => {
  const { data } = useDataState()
  const growthBook = useGrowthBook()

  switch (category) {
    case 'f01':
    case 'hopif01':
    case 'm01':
    case 'wallpif01': {
      const { age_bucket } = data[category]
      if (
        age_bucket === Question.AgeBucket.Age_40_49 ||
        age_bucket === Question.AgeBucket.Age_50_plus
      ) {
        if (
          growthBook.evalFeature('age_based_assets_enabled')?.value === true
        ) {
          return 'memojiOlder'
        }
      }
      return DEFAULT_IMAGE_VARIANT
    }
    default:
      return DEFAULT_IMAGE_VARIANT
  }
}

export const useFunnelImages = <TCategory extends ImageCategories>(
  funnel: TCategory
) => {
  const variant = useCategoryImageVariant(funnel)

  const getFunnelImageProps = useCallback(
    <
      TVariant extends CategoryVariants<TCategory>,
      TPages extends CategoryPages<TCategory, TVariant>,
      TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
    >(
      page: TPages,
      imageName: TImage,
      locale?: string
    ): ImageProps =>
      getImageProps(funnel, page, imageName, {
        variant: variant as TVariant,
        locale,
      }),
    [funnel, variant]
  )

  return { getFunnelImageProps }
}

export const useImageProps = <
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
  TPages extends CategoryPages<TCategory, TVariant>,
  TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
>(
  category: TCategory,
  page: TPages,
  imageName: TImage,
  locale?: string
): ImageProps => {
  const { getFunnelImageProps } = useFunnelImages<TCategory>(category)

  return useMemo(
    () =>
      getFunnelImageProps<TVariant, TPages, TImage>(page, imageName, locale),
    [getFunnelImageProps, page, imageName, locale]
  )
}
